<template>
  <div class="admin-product-list">
    <h2>전체 주문 조회하기</h2>
    <div class="button-box">
      <button @click="deleteOrder">※주의※ 주문삭제 처리</button>
      <button class="blue" @click="convertToStatus('BEFORE_PAYMENT')">
        결제전 처리
      </button>
      <button class="blue" @click="convertToStatus('PAYMENT_COMPLETED')">
        결제완료 처리
      </button>
      <button class="blue" @click="convertToStatus('PREPARING_PRODUCT')">
        상품준비중 처리
      </button>
      <button class="blue" @click="convertToStatus('PREPARING_DELIVERY')">
        배송준비중 처리
      </button>
      <button class="blue" @click="convertToStatus('SHIPPING_PROGRESS')">
        배송중 처리
      </button>
      <button class="blue" @click="convertToStatus('DELIVERY_COMPLETED')">
        배송완료 처리
      </button>
      <button class="red" @click="adminConvertToStatus('CANCELLED')">
        관리자 취소 처리
      </button>
      <button class="red" @click="adminConvertToStatus('EXCHANGE')">
        관리자 교환 처리
      </button>
      <button class="red" @click="adminConvertToStatus('RETURNED')">
        관리자 반품 처리
      </button>
    </div>
    <div class="table-box">
      <h3>전체 주문 목록 테이블</h3>
      <div ref="tableRef"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from "vue";
import "frappe-datatable/dist/frappe-datatable.min.css";
import DataTable from "frappe-datatable";
import {
  collection,
  query,
  orderBy,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  arrayRemove,
  writeBatch,
  Timestamp,
  addDoc,
  limit,
  where,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  deliveryCompanyFormatter,
  generateDeliveryCompanyLabel,
  generateDeliveryTrackingLinkOrigin,
  generateOrderStatusLabel,
} from "@/lib/utils";
import { sendPpurioMessage } from "@/lib/ppurio";
import { useBusyLock } from "@/lib/useBusyLock";

const { runLocked } = useBusyLock();

const tableRef = ref(null);
const dataTable = ref(null);
const originData = ref([]);

const adminConvertToStatus = (status) =>
  runLocked(async () => {
    const checkedItems = getCheckedItems(); // getCheckedItems는 동기 함수

    if (!checkedItems.length) {
      alert("주문 건이 선택되지 않았습니다!");
      return;
    }

    const validItems = checkedItems.filter((item) => {
      // 클레임이 이미 있는 건 제외 + 이미 클레임 완료 상태 제외
      const hasClaim = !!item.claimStatus;

      const isBlockedStatus =
        item.status === "CANCELLED" ||
        item.status === "EXCHANGE" ||
        item.status === "RETURNED";

      return !hasClaim && !isBlockedStatus;
    });

    if (!validItems.length) {
      alert("선택된 주문 중 유효한 항목이 없습니다.");
      return;
    }

    if (
      !confirm(`${validItems.length}건의 주문을 '${status}' 처리하시겠습니까?`)
    ) {
      return;
    }

    const now = Timestamp.fromDate(new Date());

    const promises = validItems
      .map((item) => {
        const docId = item.productOrderId || item.id;
        if (!docId) {
          console.warn("문서 ID 없음:", item);
          return null;
        }

        let claimReason = "";
        let claimStatus = "";

        switch (status) {
          case "CANCELLED":
            claimReason = "관리자 취소 처리";
            claimStatus = "PROCESSING_CANCEL";
            break;
          case "EXCHANGE":
            claimReason = "관리자 교환 처리";
            claimStatus = "PROCESSING_EXCHANGE";
            break;
          case "RETURNED":
            claimReason = "관리자 반품 처리";
            claimStatus = "PROCESSING_RETURN";
            break;
          default:
            break;
        }

        return updateDoc(doc(db, "productOrder", docId), {
          status,
          claimReason,
          claimStatus,
          claimProcessedAt: now,
          claimRequestedAt: now,
          claimType: "ADMIN",
        });
      })
      .filter(Boolean);

    await Promise.all(promises);

    alert(`${validItems.length}건의 주문을 '${status}' 처리하였습니다.`);
    window.location.reload();
  }).catch((error) => {
    console.error("주문 상태 병렬 처리 중 오류:", error);
    alert("상태 변경 중 오류가 발생했습니다.");
  });

const deleteOrder = () =>
  runLocked(async () => {
    const checkedItems = getCheckedItems(); // 동기 함수

    if (!checkedItems.length) {
      alert("주문 건이 선택되지 않았습니다!");
      return;
    }

    const ok = confirm(
      `정말로 ${checkedItems.length}건의 주문 건을 삭제하시겠습니까?\n이 작업은 취소할 수 없습니다.`
    );
    if (!ok) return;

    // 작업 수 대략 체크 (order update/delete + productOrder delete = 최소 2개씩)
    // Firestore writeBatch는 500개 제한
    if (checkedItems.length > 200) {
      alert(
        "한 번에 삭제할 수 있는 건수가 너무 많습니다. (권장: 200건 이하)\n나눠서 다시 시도해주세요."
      );
      return;
    }

    const batch = writeBatch(db);

    for (const item of checkedItems) {
      const orderId = item.orderId;
      const productOrderId = item.productOrderId;

      if (!orderId || !productOrderId) {
        console.warn("orderId/productOrderId 누락:", item);
        continue;
      }

      const orderRef = doc(db, "order", orderId);
      const productOrderRef = doc(db, "productOrder", productOrderId);

      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        // order 문서가 없어도 productOrder는 삭제 시도
        batch.delete(productOrderRef);
        continue;
      }

      const { productOrders = [] } = orderSnap.data() || {};

      if (productOrders.length > 1) {
        // order 문서는 유지, 배열에서 productOrderId만 제거
        batch.update(orderRef, {
          productOrders: arrayRemove(productOrderId),
        });
      } else {
        // 마지막 상품주문이면 order 문서 삭제
        batch.delete(orderRef);
      }

      // 항상 productOrder 문서 삭제
      batch.delete(productOrderRef);
    }

    await batch.commit();

    alert(`${checkedItems.length}건의 주문 건을 삭제처리했습니다!`);
    window.location.reload();
  }).catch((error) => {
    console.error("주문 삭제처리 실패:", error);
    alert("삭제 중 오류가 발생했습니다. 콘솔 로그를 확인해 주세요.");
  });

const convertToStatus = (after) =>
  runLocked(async () => {
    const checkedItems = getCheckedItems();

    if (!checkedItems.length) {
      alert("주문 건이 선택되지 않았습니다!");
      return;
    }

    // claim 진행/완료 건 제외 (빈문자열/undefined/null 모두 허용하려면 !item.claimStatus)
    const validItems = checkedItems.filter((item) => !item.claimStatus);

    if (!validItems.length) {
      alert("선택된 주문 중 유효한 항목이 없습니다.");
      return;
    }

    if (
      !confirm(`${validItems.length}건의 주문을 '${after}' 처리하시겠습니까?`)
    ) {
      return;
    }

    // ✅ 문자 발송 여부는 1회만 확인
    const shouldSendMessage = confirm("고객님께 메시지를 보내겠습니까?");

    const now = Timestamp.fromDate(new Date());

    // =========================
    // 1) productOrder 기준 상태 업데이트 + 포인트 지급
    // =========================
    await Promise.all(
      validItems.map(async (item) => {
        const docId = item.productOrderId || item.id;
        if (!docId) {
          console.warn("문서 ID 없음:", item);
          return;
        }

        // productOrder 업데이트 데이터
        const productOrderUpdateData = {
          status: after,
        };

        // 배송완료 시 포인트 지급 여부 마킹
        if (after === "DELIVERY_COMPLETED") {
          productOrderUpdateData.pointGived = true;
        }

        await updateDoc(doc(db, "productOrder", docId), productOrderUpdateData);

        // 배송완료 시 포인트 지급 (상품주문서 기준)
        if (after === "DELIVERY_COMPLETED" && item.userId) {
          const point = Math.floor(Number(item.pointAmount || 0));

          // 이미 지급된 건 재지급 방지 (데이터에 pointGived가 있다면)
          if (point > 0 && !item.pointGived) {
            const lastUserPointDocs = await getDocs(
              query(
                collection(db, "userPoints"),
                where("userId", "==", item.userId),
                orderBy("createdAt", "desc"),
                limit(1)
              )
            );

            const lastPoint = lastUserPointDocs.empty
              ? 0
              : Number(lastUserPointDocs.docs[0].data()?.afterPoint || 0);

            await addDoc(collection(db, "userPoints"), {
              actionType: "ADD",
              afterPoint: lastPoint + point,
              amount: point,
              beforePoint: lastPoint,
              createdAt: now,
              description: "구매확정으로 인한 포인트 지급",
              sourceType: "ORDER_REWARD",
              userId: item.userId,
            });
          }
        }
      })
    );

    // =========================
    // 2) 문자 발송은 order 기준 1회만
    // =========================
    if (shouldSendMessage) {
      const uniqueOrderIds = [
        ...new Set(validItems.map((item) => item.orderId).filter(Boolean)),
      ];

      await Promise.all(
        uniqueOrderIds.map(async (orderId) => {
          const orderSnap = await getDoc(doc(db, "order", orderId));
          if (!orderSnap.exists()) {
            console.warn("주문 문서 없음:", orderId);
            return;
          }

          const orderData = orderSnap.data();
          if (!orderData?.phone) {
            console.warn("주문자 전화번호 없음:", orderId);
            return;
          }

          // 같은 주문의 대표 상품주문서 1개 (배송정보/포인트 등 표시용)
          const representativeItem = validItems.find(
            (v) => v.orderId === orderId
          );
          if (!representativeItem) return;

          switch (after) {
            case "PAYMENT_COMPLETED":
              await sendPpurioMessage({
                targets: [{ to: orderData.phone }],
                targetCount: 1,
                content: `[네코쿠모] 입금 처리가 완료되었습니다.\n\n주문번호: ${orderId}\n\n빠르게 발주 완료 후 연락 남겨드리겠습니다. 감사합니다!`,
                refKey: `PC_${orderId}`,
              });
              break;

            case "PREPARING_PRODUCT":
              await sendPpurioMessage({
                targets: [{ to: orderData.phone }],
                targetCount: 1,
                content: `[네코쿠모] 상품 발주 처리가 완료되었습니다.\n\n주문번호: ${orderId}\n\n빠르게 상품 수령 및 검수 완료 후 연락 남겨드리겠습니다. 감사합니다!`,
                refKey: `PP_${orderId}`,
              });
              break;

            case "PREPARING_DELIVERY":
              await sendPpurioMessage({
                targets: [{ to: orderData.phone }],
                targetCount: 1,
                content: `[네코쿠모] 상품 검수 처리가 완료되었습니다.\n\n주문번호: ${orderId}\n\n포장 및 발송 후 연락 남겨드리겠습니다. 감사합니다!`,
                refKey: `PD_${orderId}`,
              });
              break;

            case "SHIPPING_PROGRESS":
              await sendPpurioMessage({
                targets: [{ to: orderData.phone }],
                targetCount: 1,
                content: `[네코쿠모] 상품 포장 및 발송 처리가 완료되었습니다.\n\n주문번호: ${orderId}\n배송정보: ${generateDeliveryCompanyLabel(
                  representativeItem.deliveryCompany
                )} ${
                  representativeItem.deliveryTrackingNumber
                }\n배송조회 링크: ${
                  representativeItem.deliveryTrackingLink || "-"
                }\n\n배송완료 시 연락 남겨드리겠습니다. 감사합니다!`,
                refKey: `SP_${orderId}`,
              });
              break;

            case "DELIVERY_COMPLETED": {
              // 같은 주문의 유효 항목 포인트 합산 표시 (문자는 주문당 1번)
              const totalPoint = validItems
                .filter((v) => v.orderId === orderId)
                .reduce(
                  (sum, v) => sum + Math.floor(Number(v.pointAmount || 0)),
                  0
                );

              await sendPpurioMessage({
                targets: [{ to: orderData.phone }],
                targetCount: 1,
                content: `[네코쿠모] 상품 배송이 완료되었습니다.\n\n주문번호: ${orderId}\n적립 냥코인: ${totalPoint.toLocaleString()}냥코인\n\n즐겁게 베이핑하시고 문제가 있으시면 언제든 연락주세요. 감사합니다!`,
                refKey: `DC_${orderId}`,
              });
              break;
            }

            default:
              break;
          }
        })
      );
    }

    alert(`${validItems.length}건의 주문을 '${after}' 처리하였습니다.`);
    window.location.reload();
  }).catch((error) => {
    console.error("주문 상태 병렬 처리 중 오류:", error);
    alert("상태 변경 중 오류가 발생했습니다.");
  });

const getCheckedItems = () => {
  try {
    const checkedRows = dataTable.value?.rowmanager?.getCheckedRows?.() || [];

    return checkedRows
      .map((rowIndex) => {
        const i = Number(rowIndex);
        const item = originData.value?.[i];
        if (!item) {
          console.warn("선택 인덱스에 해당하는 데이터가 없습니다:", rowIndex);
          return null;
        }
        console.log("Checked item:", item);
        return item;
      })
      .filter(Boolean);
  } catch (error) {
    console.error("Error getting checked items:", error);
    return [];
  }
};

const saveProductOrderField = async (
  orderId,
  field,
  value,
  failMessage = "저장 실패"
) => {
  try {
    if (!orderId) {
      console.warn("orderId 없음");
      return false;
    }

    await updateDoc(doc(db, "productOrder", orderId), {
      [field]: value,
    });

    return true;
  } catch (err) {
    alert(failMessage);
    console.error(err);
    return false;
  }
};

const handleSelectChange = async (e) => {
  const orderId = e.target.getAttribute("data-id");
  const value = e.target.value;

  const ok = await saveProductOrderField(
    orderId,
    "deliveryCompany",
    value,
    "배송업체 저장 실패"
  );

  if (!ok) return;

  console.log(`📦 배송업체 저장됨: ${value}`);
  await setupDeliveryTrackingLink(orderId);
};

const handleInputChange = async (e) => {
  const orderId = e.target.getAttribute("data-id");
  const value = e.target.value;

  const ok = await saveProductOrderField(
    orderId,
    "deliveryTrackingNumber",
    value,
    "운송장번호 저장 실패"
  );

  if (!ok) return;

  console.log(`📦 운송장번호 저장됨: ${value}`);
  await setupDeliveryTrackingLink(orderId);
};

const handleReceiptInputChange = async (e) => {
  const orderId = e.target.getAttribute("data-id");
  const value = e.target.value;

  const ok = await saveProductOrderField(
    orderId,
    "cashReceiptNumber",
    value,
    "현금영수증 번호 저장 실패"
  );

  if (!ok) return;

  console.log(`📦 현금영수증 발급번호 저장됨: ${value}`);
};

const handleCardReceiptInputChange = async (e) => {
  const orderId = e.target.getAttribute("data-id");
  const value = e.target.value;

  const ok = await saveProductOrderField(
    orderId,
    "cardReceiptNumber",
    value,
    "카드청구서 번호 저장 실패"
  );

  if (!ok) return;

  console.log(`📦 카드청구서 발급번호 저장됨: ${value}`);
};

const setupDeliveryTrackingLink = async (orderId) => {
  try {
    if (!orderId) return;

    const snap = await getDoc(doc(db, "productOrder", orderId));
    if (!snap.exists()) return;

    const data = snap.data();
    const company = data.deliveryCompany || "";
    const trackingNo = data.deliveryTrackingNumber || "";

    // 둘 중 하나라도 없으면 링크 비우기(선택)
    if (!company || !trackingNo) {
      await updateDoc(doc(db, "productOrder", orderId), {
        deliveryTrackingLink: "",
      });
      return;
    }

    const origin = generateDeliveryTrackingLinkOrigin(company);
    if (!origin) {
      await updateDoc(doc(db, "productOrder", orderId), {
        deliveryTrackingLink: "",
      });
      return;
    }

    const url = `${origin}${trackingNo}`;

    await updateDoc(doc(db, "productOrder", orderId), {
      deliveryTrackingLink: url,
    });

    console.log(`📦 배송조회 링크 저장됨: ${url}`);
  } catch (err) {
    alert("배송조회 링크 저장 실패");
    console.error(err);
  }
};

// 바인딩 함수 정의
function bindDeliverySelects() {
  // 이전 바인딩 제거
  document.querySelectorAll(".delivery-select").forEach((select) => {
    select.removeEventListener("change", handleSelectChange);
    select.addEventListener("change", handleSelectChange);
  });
}

function bindDTNInputs() {
  // 이전 바인딩 제거
  document.querySelectorAll(".delivery-tracking-number").forEach((select) => {
    select.removeEventListener("blur", handleInputChange);
    select.addEventListener("blur", handleInputChange);
  });
}

function bindCRNInputs() {
  // 이전 바인딩 제거
  document.querySelectorAll(".cash-receipt-number").forEach((select) => {
    select.removeEventListener("blur", handleReceiptInputChange);
    select.addEventListener("blur", handleReceiptInputChange);
  });
}

function bindCardRNInputs() {
  // 이전 바인딩 제거
  document.querySelectorAll(".card-receipt-number").forEach((select) => {
    select.removeEventListener("blur", handleCardReceiptInputChange);
    select.addEventListener("blur", handleCardReceiptInputChange);
  });
}

onMounted(() => {
  runLocked(async () => {
    // 1) productOrder 조회
    const q = query(
      collection(db, "productOrder"),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);

    originData.value = querySnapshot.docs.map((docSnap) => ({
      ...docSnap.data(),
      id: docSnap.id, // productOrder 문서 ID
    }));

    // 2) user / order 병렬 조회 (전체)
    await Promise.all(
      originData.value.map(async (item) => {
        try {
          const [userSnap, orderSnap] = await Promise.all([
            item.userId
              ? getDoc(doc(db, "users", item.userId))
              : Promise.resolve(null),
            item.orderId
              ? getDoc(doc(db, "order", item.orderId))
              : Promise.resolve(null),
          ]);

          let userData = null;
          let orderData = null;

          if (userSnap && userSnap.exists()) {
            userData = {
              id: userSnap.id,
              ...userSnap.data(),
            };
          }

          if (orderSnap && orderSnap.exists()) {
            orderData = {
              id: orderSnap.id,
              ...orderSnap.data(),
            };
          }

          item.userData = userData;
          item.orderData = orderData;
        } catch (e) {
          console.error("주문 부가정보 로딩 실패:", item?.id, e);
          item.userData = null;
          item.orderData = null;
        }
      })
    );

    // 3) datatable 데이터 구성
    const data = originData.value.map((item) => [
      { content: item.orderChannel || "", editable: false },
      { content: item.orderId || "", editable: false },
      { content: item.productOrderId || item.id || "", editable: false },
      { content: item.userData, editable: false },
      { content: item.productName || "", editable: false },
      { content: item.optionName || "", editable: false },
      { content: item, editable: false }, // 실결제금액 계산용
      { content: item.orderData?.paymentMethod || "", editable: false },
      { content: item, editable: false }, // 결제상태 표시용
      { content: item.deliveryCompany || "", editable: false },
      { content: item.deliveryTrackingNumber || "", editable: false },
      { content: item.deliveryTrackingLink || "", editable: false },
      { content: item, editable: false }, // 현금영수증 입력용
      { content: item, editable: false }, // 카드청구서 입력용
      {
        content: item.createdAt?.toDate?.().toLocaleString?.() || "",
        editable: false,
      },
    ]);

    // 혹시 재생성 시 기존 테이블 제거 필요하면 여기서 처리
    if (dataTable.value) {
      try {
        dataTable.value.destroy();
      } catch (e) {
        console.warn("기존 테이블 destroy 실패(무시 가능):", e);
      }
      dataTable.value = null;
    }

    // 4) DataTable 생성
    dataTable.value = new DataTable(tableRef.value, {
      columns: [
        {
          name: "쇼핑몰",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
          format: (value) => {
            switch (value) {
              case "NECOKUMO":
                return "폐쇄몰";
              case "CAFE24":
                return "카페24";
              default:
                return "알 수 없음";
            }
          },
        },
        {
          name: "주문번호",
          editable: false,
          resizable: false,
          width: 224,
          align: "center",
        },
        {
          name: "상품 주문번호",
          editable: false,
          resizable: false,
          width: 256,
          align: "center",
          format: (value) => {
            return `<a style="color: #007bff; font-weight: 700" href="${window.location.origin}/admin/order/detail?id=${value}">${value}</a>`;
          },
        },
        {
          name: "주문자",
          editable: false,
          resizable: false,
          width: 160,
          align: "center",
          format: (value) => {
            if (!value) return "회원정보 없음";
            return `<a style="color: #007bff; font-weight: 700" href="${
              window.location.origin
            }/admin/consumer/detail?id=${value.id}">${
              value.userName || "-"
            } <span style="font-weight: 400; color: black">(${
              value.userId || "-"
            })</span></a>`;
          },
        },
        {
          name: "상품명",
          editable: false,
          resizable: false,
          width: 192,
          align: "center",
        },
        {
          name: "옵션",
          editable: false,
          resizable: false,
          width: 128,
          align: "center",
        },
        {
          name: "실결제금액",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
          format: (value) => {
            return `${Number(value?.productPrice || 0).toLocaleString()}원`;
          },
        },
        {
          name: "결제수단",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
          format: (value) => {
            if (value === "bank") return "무통장입금";
            if (value === "card") return "신용/체크카드";
            return "-";
          },
        },
        {
          name: "결제상태",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
          format: (value) => {
            return generateOrderStatusLabel(
              value?.claimStatus ? value.claimStatus : value?.status
            );
          },
        },
        {
          name: "배송업체",
          editable: true,
          resizable: false,
          width: 112,
          align: "center",
          format: deliveryCompanyFormatter,
        },
        {
          name: "운송장번호",
          editable: false,
          resizable: false,
          width: 180,
          align: "center",
          format: (value, row) => {
            // ✅ row[2] = 상품 주문번호(productOrderId) 컬럼
            const productOrderId = row?.[3]?.content || "";
            return `<input type="text" class="delivery-tracking-number" data-id="${productOrderId}" value="${
              value || ""
            }" />`;
          },
        },
        {
          name: "배송조회",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
          format: (value) => {
            if (value && /\d/.test(value)) {
              return `<a href="${value}" target="_blank" style="font-weight: 700; color: #007bff">배송조회</a>`;
            }
            return "배송정보 없음";
          },
        },
        {
          name: "현금영수증 발급번호",
          editable: false,
          resizable: false,
          width: 180,
          align: "center",
          format: (value, row) => {
            if (value?.orderData?.paymentMethod === "bank") {
              const productOrderId = row?.[2]?.content || "";
              return `<input type="text" class="cash-receipt-number" data-id="${productOrderId}" value="${
                value?.cashReceiptNumber || ""
              }" />`;
            }
            return "현금영수증 미발급";
          },
        },
        {
          name: "카드결제 청구서번호",
          editable: false,
          resizable: false,
          width: 180,
          align: "center",
          format: (value, row) => {
            if (value?.orderData?.paymentMethod === "card") {
              const productOrderId = row?.[2]?.content || "";
              return `<input type="text" class="card-receipt-number" data-id="${productOrderId}" value="${
                value?.cardReceiptNumber || ""
              }" />`;
            }
            return "카드청구서 미발급";
          },
        },
        {
          name: "주문일",
          editable: false,
          resizable: false,
          width: 192,
          align: "center",
        },
      ],
      data,
      checkboxColumn: true,
      serialNoColumn: false,
      cellHeight: 64,
      inlineFilters: true,
    });
  }).catch((error) => {
    console.error("주문 목록 로딩 실패:", error);
    alert("주문 목록을 불러오는 중 오류가 발생했습니다.");
  });
});

watch(
  () => dataTable.value,
  (newTable) => {
    if (!newTable) return;
    nextTick(() => {
      console.log("✅ dataTable 변경 감지됨. 이벤트 재바인딩");
      bindDeliverySelects();
      bindDTNInputs();
      bindCRNInputs();
      bindCardRNInputs();
    });
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.admin-product-list {
  margin-top: 36px;

  > .button-box {
    margin-top: 24px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    > button {
      padding: 8px 16px;
      border-radius: 4px;
      border: none;
      font-weight: 500;
      cursor: pointer;
      font-size: 14px;

      &.red {
        background-color: #dc3545;
        color: white;

        &:hover {
          background-color: #c82333;
        }
      }

      &.blue {
        background-color: #007bff;
        color: white;

        &:hover {
          background-color: #0056b3;
        }
      }

      &.secondary {
        background-color: #6c757d;
        color: white;

        &:hover {
          background-color: #545b62;
        }
      }
    }
  }

  > .table-box {
    margin-top: 24px;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.25);
    border-radius: 8px;
    padding: 24px;

    > div {
      margin-top: 16px;
      width: 100%;
      height: fit-content;
    }
  }

  > dialog {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: #fff;
    border-radius: 8px;
    width: 90%;
    height: 90%;
    max-width: 600px;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.25);

    &::backdrop {
      background: rgba(0, 0, 0, 0.5);
    }

    > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 16px;
      position: sticky;
      top: 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.25);
      background: #efefef;

      > h2 {
        font-size: 18px;
      }

      > button {
        border: none;
        border-radius: 4px;
        padding: 4px 8px;
        background-color: #007bff;
        color: #fff;
        font-weight: 700;
        cursor: pointer;

        > span {
          font-size: 16px;
        }
      }
    }

    > main {
      padding: 16px;
      > textarea {
        width: 100%;
        height: 300px;
        padding: 8px;
        border-radius: 4px;
        border: 1px solid #ccc;
      }

      button {
        border: none;
        border-radius: 4px;
        padding: 6px 12px;
        background-color: #007bff;
        color: #fff;
        font-weight: 500;
        cursor: pointer;
        font-size: 14px;
      }
    }
  }
}
</style>
